/**
 * The Sales Forecast Upload's endpoints (`docs/UTILITIES.md` §5), through the
 * one request path in `lib/api.ts` so errors arrive as the same `ApiError`.
 */

import { request } from '../../lib/api'

export const BASE = '/api/utilities/sales-forecast-upload'

export interface Load {
  readonly load_id: number
  readonly fcst: string
  readonly fiscal_year: number
  readonly source_file: string
  readonly loaded_by: string
  readonly loaded_at: string
  readonly lines: number
  readonly gal: number
  readonly cases: number
  readonly fob: number
  readonly first_period: string
  readonly last_period: string
  readonly is_current: boolean
  readonly superseded_at: string | null
  readonly superseded_by: number | null
}

export interface Landing {
  readonly slug: string
  readonly fcst_types: readonly string[]
  readonly max_upload_bytes: number
  readonly staging_ttl_seconds: number
  readonly loads: readonly Load[]
}

export interface Totals {
  readonly lines: number
  readonly gal: number
  readonly cases: number
  readonly fob: number
}

export interface Difference extends Totals {
  readonly gal_pct: number | null
}

export interface MonthComparison {
  readonly period: string
  readonly label: string
  readonly fiscal_month: number
  readonly in_window: boolean
  readonly current: Totals | null
  readonly proposed: Totals
  readonly difference: Difference | null
}

export interface ComparisonTotal {
  readonly current: Totals | null
  readonly proposed: Totals
  readonly difference: Difference | null
}

export interface Comparison {
  readonly fcst: string
  readonly fiscal_year: number
  readonly has_current: boolean
  readonly months: readonly MonthComparison[]
  readonly total: ComparisonTotal
}

export interface Issue {
  readonly severity: 'blocker' | 'warning'
  readonly code: string
  readonly message: string
  readonly examples: readonly string[]
  readonly count: number | null
}

export interface PlaceholderCandidate {
  readonly sku: string
  readonly lines: number
  readonly gal: number
  readonly in_sku_map: boolean
  readonly flagged_before: boolean
  readonly suggested: boolean
}

export interface Preview {
  readonly fcst: string
  readonly fiscal_year: number | null
  readonly source_file: string
  readonly sheet: string | null
  readonly sha256: string
  readonly size_bytes: number
  readonly blocked: boolean
  readonly issues: readonly Issue[]
  readonly current_load: Load | null
  readonly comparison: Comparison | null
  readonly placeholder_candidates: readonly PlaceholderCandidate[]
  readonly staging_token: string | null
  readonly expected_current_load_id: number | null
}

export interface LoadedMonth extends Totals {
  readonly period: string
  readonly label: string
}

export interface CommitResult {
  readonly load: Load
  readonly replaced: Load | null
  readonly archived_lines: number
  readonly months: readonly LoadedMonth[]
}

export const forecastApi = {
  landing: (): Promise<Landing> => request<Landing>(BASE),

  preview: (fcst: string, file: File): Promise<Preview> => {
    const form = new FormData()
    form.append('fcst', fcst)
    form.append('file', file, file.name)
    return request<Preview>(`${BASE}/preview`, { method: 'POST', body: form })
  },

  commit: (preview: Preview, placeholderSkus: readonly string[]): Promise<CommitResult> =>
    request<CommitResult>(`${BASE}/commit`, {
      method: 'POST',
      body: JSON.stringify({
        staging_token: preview.staging_token,
        expected_current_load_id: preview.expected_current_load_id,
        placeholder_skus: placeholderSkus,
      }),
    }),

  /** A plain link: the browser downloads it with the session cookie. */
  downloadUrl: (loadId: number): string => `${BASE}/loads/${String(loadId)}/download`,
}
